import React, { useState } from 'react';
import { useProject } from '../context/ProjectContext';
import { componentTypes } from '../data/manufacturers';
import {
  ChevronRight,
  ChevronDown,
  Search,
  Plus,
  Trash2,
  FolderOpen,
  Folder,
  MoreVertical,
  Zap,
  ToggleRight,
  ToggleLeft,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Gauge,
  Square,
  Settings,
  Minus,
  Cable,
  ShieldOff,
  Activity,
  RefreshCw,
  Cpu,
  Sun,
  Repeat,
  Timer,
  Wifi,
  Lightbulb,
  Circle,
  Layers,
  LayoutGrid,
  Expand,
  Shrink,
  Radio,
  Sliders,
  GitBranch,
  Battery,
  BatteryCharging,
  BatteryFull,
  Touchpad,
  Copy,
  GripVertical
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger
} from './ui/dropdown-menu';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger
} from './ui/context-menu';

const iconMap = {
  Zap, ToggleRight, ToggleLeft, Shield, ShieldCheck, ShieldAlert,
  Gauge, Square, Settings, Minus, Cable, ShieldOff, Activity,
  RefreshCw, Cpu, Sun, Repeat, Timer, Wifi, Lightbulb, Circle, Layers, LayoutGrid,
  Radio, Sliders, GitBranch, Battery, BatteryCharging, BatteryFull, Touchpad
};

const TreeNode = ({ node, depth = 0, onSelect, onContextMenu, onDragStart, onDragOver, onDrop, onDropBefore, draggedNode }) => {
  const { selectedNode, expandedNodes, toggleNodeExpanded, addNode, deleteNode, duplicateNode, moveNode, moveNodeBefore } = useProject();
  const isSelected = selectedNode?.id === node.id;
  const isExpanded = expandedNodes.has(node.id);
  const hasChildren = node.children && node.children.length > 0;
  const componentType = componentTypes[node.type];
  const IconComponent = iconMap[componentType?.icon] || Square;
  const [isDragOver, setIsDragOver] = React.useState(false);

  const handleClick = (e) => {
    e.stopPropagation();
    onSelect(node);
  };

  const handleToggle = (e) => {
    e.stopPropagation();
    toggleNodeExpanded(node.id);
  };

  const handleAddComponent = (type) => {
    addNode(node.id, type, { bezeichnung: `Neue ${componentTypes[type]?.name || 'Komponente'}` });
  };

  const handleDelete = () => {
    if (window.confirm(`"${node.bezeichnung}" wirklich löschen?`)) {
      deleteNode(node.id);
    }
  };

  const handleDuplicate = () => {
    duplicateNode(node.id);
  };

  // Drag & Drop Handlers
  const handleDragStart = (e) => {
    e.stopPropagation();
    e.dataTransfer.setData('text/plain', node.id);
    e.dataTransfer.effectAllowed = 'move';
    onDragStart?.(node);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedNode?.id !== node.id) {
      setIsDragOver(true);
      e.dataTransfer.dropEffect = 'move';
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDropOnNode = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const draggedId = e.dataTransfer.getData('text/plain');
    if (draggedId && draggedId !== node.id) {
      // Verschiebe als Kind dieses Knotens
      moveNode(draggedId, node.id);
    }
    onDrop?.();
  };

  // Drop-Zone zwischen Elementen
  const [isDropBefore, setIsDropBefore] = React.useState(false);
  
  const handleDropBefore = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDropBefore(false);
    const draggedId = e.dataTransfer.getData('text/plain');
    if (draggedId && draggedId !== node.id) {
      moveNodeBefore(draggedId, node.id);
    }
    onDrop?.();
  };

  const AddComponentMenu = ({ trigger }) => (
    <ContextMenuSub>
      <ContextMenuSubTrigger>{trigger}</ContextMenuSubTrigger>
      <ContextMenuSubContent className="w-56 max-h-96 overflow-y-auto">
        {Object.entries(componentTypes).map(([key, type]) => {
          const TypeIcon = iconMap[type.icon] || Square;
          return (
            <ContextMenuItem key={key} onClick={() => handleAddComponent(key)}>
              <TypeIcon className="w-4 h-4 mr-2" style={{ color: type.color }} />
              {type.name}
            </ContextMenuItem>
          );
        })}
      </ContextMenuSubContent>
    </ContextMenuSub>
  );

  return (
    <div>
      {/* Drop-Zone ZWISCHEN Elementen (vor diesem Element) */}
      {draggedNode && draggedNode.id !== node.id && (
        <div
          className={`h-1 mx-2 rounded transition-all ${isDropBefore ? 'bg-blue-500 h-2' : 'bg-transparent hover:bg-blue-200'}`}
          style={{ marginLeft: `${depth * 16 + 8}px` }}
          onDragOver={(e) => { e.preventDefault(); setIsDropBefore(true); }}
          onDragLeave={() => setIsDropBefore(false)}
          onDrop={handleDropBefore}
        />
      )}
      <ContextMenu>
        <ContextMenuTrigger>
          <div
            className={`flex items-center py-1 px-2 cursor-grab hover:bg-emerald-50 border-l-2 transition-colors ${
              isSelected ? 'bg-emerald-100 border-l-emerald-500' : 'border-l-transparent'
            } ${isDragOver ? 'bg-blue-100 border-l-blue-500' : ''}`}
            style={{ paddingLeft: `${depth * 16 + 8}px` }}
            onClick={handleClick}
            draggable
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDropOnNode}
          >
            {/* Expand/Collapse Toggle */}
            <div className="w-5 h-5 flex items-center justify-center mr-1">
              {hasChildren ? (
                <button onClick={handleToggle} className="hover:bg-emerald-200 rounded p-0.5">
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-500" />
                  )}
                </button>
              ) : (
                <span className="w-4" />
              )}
            </div>

            {/* Icon - NUR das Komponenten-Typensymbol */}
            <div className="mr-2 flex items-center">
              <IconComponent
                className="w-4 h-4"
                style={{ color: componentType?.color || '#666' }}
              />
            </div>

            {/* Label */}
            <span className="text-sm text-gray-700 truncate flex-1">
              {node.bezeichnung}
            </span>

            {/* Type Badge */}
            <span className="text-xs text-gray-400 ml-2 hidden group-hover:block">
              {componentType?.name}
            </span>
          </div>
        </ContextMenuTrigger>

        <ContextMenuContent className="w-56">
          <AddComponentMenu trigger="Hinzufügen" />
          <ContextMenuSeparator />
          <ContextMenuItem onClick={() => onSelect(node)}>
            <Settings className="w-4 h-4 mr-2" />
            Bearbeiten
          </ContextMenuItem>
          <ContextMenuItem onClick={handleDuplicate}>
            <Copy className="w-4 h-4 mr-2" />
            Duplizieren
          </ContextMenuItem>
          <ContextMenuItem onClick={() => toggleNodeExpanded(node.id)}>
            {isExpanded ? (
              <><Shrink className="w-4 h-4 mr-2" />Zuklappen</>
            ) : (
              <><Expand className="w-4 h-4 mr-2" />Aufklappen</>
            )}
          </ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuItem onClick={handleDelete} className="text-red-600">
            <Trash2 className="w-4 h-4 mr-2" />
            Löschen
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>

      {/* Children */}
      {isExpanded && hasChildren && (
        <div>
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              onSelect={onSelect}
              onContextMenu={onContextMenu}
              onDragStart={onDragStart}
              onDrop={onDrop}
              onDropBefore={onDropBefore}
              draggedNode={draggedNode}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ProjectTree = () => {
  const {
    currentProject,
    selectedNode,
    setSelectedNode,
    addNode,
    deleteNode,
    expandAll,
    collapseAll,
    moveNode
  } = useProject();
  const [searchTerm, setSearchTerm] = useState('');
  const [draggedNode, setDraggedNode] = useState(null);

  const handleDragStart = (node) => {
    setDraggedNode(node);
  };

  const handleDrop = () => {
    setDraggedNode(null);
  };

  const handleAddRootComponent = (type) => {
    addNode(null, type, { bezeichnung: `Neue ${componentTypes[type]?.name || 'Komponente'}` });
  };

  const handleDeleteSelected = () => {
    if (selectedNode && window.confirm(`"${selectedNode.bezeichnung}" wirklich löschen?`)) {
      deleteNode(selectedNode.id);
    }
  };

  const filterNodes = (nodes, term) => {
    if (!term) return nodes;
    return nodes.filter(node => {
      const matches = node.bezeichnung.toLowerCase().includes(term.toLowerCase());
      const hasMatchingChildren = node.children?.length > 0 && filterNodes(node.children, term).length > 0;
      return matches || hasMatchingChildren;
    }).map(node => ({
      ...node,
      children: node.children ? filterNodes(node.children, term) : []
    }));
  };

  const filteredNodes = filterNodes(currentProject?.nodes || [], searchTerm);

  return (
    <div className="flex flex-col h-full bg-white border-r border-gray-200">
      {/* Toolbar */}
      <div className="flex items-center gap-2 p-2 border-b border-gray-200 bg-gray-50">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="text-xs">
              Aktionen
              <ChevronDown className="w-3 h-3 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48">
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Plus className="w-4 h-4 mr-2" />
                Hinzufügen
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-56 max-h-96 overflow-y-auto">
                {Object.entries(componentTypes).map(([key, type]) => {
                  const TypeIcon = iconMap[type.icon] || Square;
                  return (
                    <DropdownMenuItem key={key} onClick={() => handleAddRootComponent(key)}>
                      <TypeIcon className="w-4 h-4 mr-2" style={{ color: type.color }} />
                      {type.name}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={expandAll}>
              <Expand className="w-4 h-4 mr-2" />
              Alle aufklappen
            </DropdownMenuItem>
            <DropdownMenuItem onClick={collapseAll}>
              <Shrink className="w-4 h-4 mr-2" />
              Alle zuklappen
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          variant="destructive"
          size="sm"
          className="text-xs"
          onClick={handleDeleteSelected}
          disabled={!selectedNode}
        >
          Löschen
        </Button>

        <Button variant="outline" size="sm" className="text-xs ml-auto">
          <MoreVertical className="w-3 h-3" />
        </Button>
      </div>

      {/* Tree */}
      <div className="flex-1 overflow-auto">
        {/* Rechtsklick auf leeren Bereich */}
        <ContextMenu>
          <ContextMenuTrigger className="min-h-full">
            <div className="py-2">
              {filteredNodes.map((node) => (
                <TreeNode
                  key={node.id}
                  node={node}
                  onSelect={setSelectedNode}
                  onContextMenu={() => {}}
                  onDragStart={handleDragStart}
                  onDrop={handleDrop}
                  draggedNode={draggedNode}
                />
              ))}
              {filteredNodes.length === 0 && (
                <div className="text-center text-gray-400 py-8 text-sm">
                  {searchTerm ? 'Keine Treffer' : 'Rechtsklick zum Hinzufügen'}
                </div>
              )}
            </div>
          </ContextMenuTrigger>
          <ContextMenuContent className="w-56">
            <ContextMenuSub>
              <ContextMenuSubTrigger>
                <Plus className="w-4 h-4 mr-2" />
                Hinzufügen
              </ContextMenuSubTrigger>
              <ContextMenuSubContent className="w-56 max-h-96 overflow-y-auto">
                {Object.entries(componentTypes).map(([key, type]) => {
                  const TypeIcon = iconMap[type.icon] || Square;
                  return (
                    <ContextMenuItem key={key} onClick={() => handleAddRootComponent(key)}>
                      <TypeIcon className="w-4 h-4 mr-2" style={{ color: type.color }} />
                      {type.name}
                    </ContextMenuItem>
                  );
                })}
              </ContextMenuSubContent>
            </ContextMenuSub>
          </ContextMenuContent>
        </ContextMenu>
      </div>

      {/* Search */}
      <div className="p-2 border-t border-gray-200">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          )}
        </div>
        <div className="flex gap-1 mt-2">
          <Button variant="ghost" size="sm" className="text-xs flex-1" onClick={expandAll}>
            <Expand className="w-3 h-3 mr-1" />
          </Button>
          <Button variant="ghost" size="sm" className="text-xs flex-1" onClick={collapseAll}>
            <Shrink className="w-3 h-3 mr-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProjectTree;
